/* ============================================================
   MC CART — persistent bag + slide-in drawer
   ------------------------------------------------------------
   Works on both renderers:
     · the React home page already draws a bag button, but its
       drawer is a shell that always reads "Your bag is empty".
       We adopt that button: same icon, real cart behind it.
     · the Liquid templates have no bag at all, so one is added
       to .mc-nav-in.
   Falls back to a floating pill if neither header is found.

   Market safe: every request goes through Shopify.routes.root,
   so /en-eu, /en-gb, /en-ch and /en-me keep their prefix and no
   redirect hop is added.

   Defensive throughout: any failure leaves the native Shopify
   behaviour (form post to /cart/add, redirect to /cart) intact.
   ============================================================ */
(function () {
  'use strict';

  var root = '/';
  try {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      root = window.Shopify.routes.root;
    }
  } catch (e) {}
  if (root.charAt(root.length - 1) !== '/') root += '/';

  var cart = null;          // last known cart.js payload
  var drawer = null;        // drawer root element
  var panel = null;         // sliding panel
  var body = null;          // drawer body
  var pill = null;          // our own bag button, for the Liquid pages
  var bags = [];            // every bag trigger on the page, each with a badge
  var lastFocus = null;
  var busy = false;

  /* ---------------------------------------------------------- styles */

  var CSS = [
    '.mcb{position:relative;display:inline-flex;align-items:center;justify-content:center;gap:8px;height:40px;padding:0 6px;background:none;border:0;cursor:pointer;color:inherit;font:inherit;-webkit-appearance:none;appearance:none}',
    '.mcb svg{width:19px;height:19px;stroke:currentColor;fill:none;stroke-width:1.4}',
    '.mcb:focus-visible{outline:2px solid currentColor;outline-offset:3px;border-radius:4px}',
    '.mcb-badge{position:absolute;top:2px;right:-2px;min-width:17px;height:17px;padding:0 4px;border-radius:999px;background:#07111F;color:#fff;font-family:Jost,system-ui,sans-serif;font-size:10px;font-weight:600;line-height:17px;text-align:center;letter-spacing:0;box-shadow:0 0 0 2px rgba(255,255,255,.9)}',
    '.mcb-badge[hidden]{display:none}',
    '.mcb-float{position:fixed;top:14px;right:14px;z-index:2147483000;color:#07111F;background:rgba(255,255,255,.92);border-radius:999px;padding:0 12px;height:44px;-webkit-backdrop-filter:blur(12px);backdrop-filter:blur(12px);box-shadow:0 6px 22px rgba(7,17,31,.16)}',
    '.mcd{position:fixed;inset:0;z-index:2147483600;visibility:hidden}',
    '.mcd.is-on{visibility:visible}',
    '.mcd-veil{position:absolute;inset:0;background:rgba(7,17,31,.42);opacity:0;-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);transition:opacity .32s ease}',
    '.mcd.is-on .mcd-veil{opacity:1}',
    '.mcd-panel{position:absolute;top:0;right:0;height:100%;width:min(420px,100vw);display:flex;flex-direction:column;background:#fff;color:#0D1B2E;transform:translateX(100%);transition:transform .38s cubic-bezier(.22,.61,.36,1);box-shadow:-18px 0 44px rgba(7,17,31,.16)}',
    '.mcd.is-on .mcd-panel{transform:none}',
    '@media (prefers-reduced-motion:reduce){.mcd-veil,.mcd-panel{transition:none}}',
    '.mcd-top{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:22px 24px 16px;border-bottom:1px solid rgba(13,27,46,.10)}',
    '.mcd-ttl{margin:0;font-family:Jost,system-ui,sans-serif;font-size:12px;font-weight:500;letter-spacing:.24em;text-transform:uppercase;color:#0D1B2E}',
    '.mcd-x{background:none;border:0;cursor:pointer;padding:6px;margin:-6px;color:#5C6678;font-size:11px;letter-spacing:.16em;text-transform:uppercase;font-family:Jost,system-ui,sans-serif}',
    '.mcd-x:hover{color:#0D1B2E}',
    '.mcd-x:focus-visible{outline:2px solid #0D1B2E;outline-offset:2px}',
    '.mcd-body{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:20px 24px}',
    '.mcd-empty{margin:48px 0 0;text-align:center;color:#5C6678;font-size:15px}',
    '.mcd-list{list-style:none;margin:0;padding:0;display:grid;gap:20px}',
    '.mcd-li{display:flex;gap:16px;align-items:flex-start;padding-bottom:20px;border-bottom:1px solid rgba(13,27,46,.08)}',
    '.mcd-li:last-child{border-bottom:0;padding-bottom:0}',
    '.mcd-img{flex:0 0 72px;width:72px;height:72px;object-fit:contain;background:#F6F7F9;border-radius:10px;padding:7px;mix-blend-mode:multiply}',
    '.mcd-info{flex:1;min-width:0}',
    '.mcd-name{display:block;font-family:Jost,system-ui,sans-serif;font-size:12px;letter-spacing:.09em;text-transform:uppercase;color:#0D1B2E;text-decoration:none;line-height:1.45}',
    '.mcd-name:hover{text-decoration:underline}',
    '.mcd-var{margin:5px 0 0;font-size:12px;color:#5C6678}',
    '.mcd-row{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:11px}',
    '.mcd-qty{display:inline-flex;align-items:center;border:1px solid rgba(13,27,46,.18);border-radius:999px}',
    '.mcd-qty button{width:30px;height:30px;background:none;border:0;cursor:pointer;color:#0D1B2E;font-size:15px;line-height:1;padding:0}',
    '.mcd-qty button:disabled{opacity:.3;cursor:default}',
    '.mcd-qty button:focus-visible{outline:2px solid #0D1B2E;outline-offset:1px;border-radius:999px}',
    '.mcd-qty span{min-width:22px;text-align:center;font-size:13px;font-variant-numeric:tabular-nums}',
    '.mcd-price{font-size:13px;color:#0D1B2E;font-variant-numeric:tabular-nums}',
    '.mcd-foot{border-top:1px solid rgba(13,27,46,.10);padding:18px 24px 24px;background:#fff}',
    '.mcd-sub{display:flex;align-items:baseline;justify-content:space-between;gap:16px;margin:0 0 4px}',
    '.mcd-sub dt{font-family:Jost,system-ui,sans-serif;font-size:11px;letter-spacing:.2em;text-transform:uppercase;color:#5C6678}',
    '.mcd-sub dd{margin:0;font-family:Jost,system-ui,sans-serif;font-size:19px;color:#0D1B2E;font-variant-numeric:tabular-nums}',
    '.mcd-fine{margin:0 0 16px;font-size:11.5px;color:#5C6678;line-height:1.5}',
    '.mcd-go{display:block;width:100%;padding:16px 20px;border:0;border-radius:999px;cursor:pointer;background:#07111F;color:#fff;font-family:Jost,system-ui,sans-serif;font-size:12px;font-weight:600;letter-spacing:.22em;text-transform:uppercase;transition:transform .18s ease}',
    '.mcd-go:hover{transform:translateY(-1px)}',
    '.mcd-go:focus-visible{outline:2px solid #07111F;outline-offset:3px}',
    '.mcd-view{display:block;margin:12px 0 0;text-align:center;font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:#5C6678;font-family:Jost,system-ui,sans-serif}',
    '.mcd-err{margin:0 0 14px;padding:10px 12px;background:#FBEDED;color:#8A2A2A;font-size:13px;border-radius:8px}',
    'body.mcd-lock{overflow:hidden}'
  ].join('');

  function injectCSS() {
    var s = document.createElement('style');
    s.id = 'mc-cart-css';
    s.textContent = CSS;
    (document.head || document.documentElement).appendChild(s);
  }

  /* ---------------------------------------------------------- helpers */

  /* The storefront prints prices as €3.500,00 - symbol first, continental
     separators. No single Intl currency locale produces that exact shape, and
     a drawer that reads 3,500.00 beside a page that reads 3.500,00 looks like
     a bug. So: group the digits with the locale that matches the market, then
     put the House's own symbol in front. */
  var GROUPING = {
    EUR: 'de-DE', CHF: 'de-CH', GBP: 'en-GB', USD: 'en-US', AED: 'en-US'
  };
  var SYMBOL = {
    EUR: '€', GBP: '£', USD: '$', CHF: 'CHF ', AED: 'AED '
  };

  function money(cents, currency) {
    var cur = currency || 'EUR';
    var v = (cents || 0) / 100;
    try {
      var n = new Intl.NumberFormat(GROUPING[cur] || 'en', {
        minimumFractionDigits: 2, maximumFractionDigits: 2
      }).format(v);
      return (SYMBOL[cur] || cur + ' ') + n;
    } catch (e) {
      return v.toFixed(2) + ' ' + cur;
    }
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function itemImage(item) {
    /* The 3D configurator pushes its preview through line item properties;
       mirror what templates/cart.liquid already does for those lines. */
    var p = item.properties || {};
    if (p._THUMB) return p._THUMB;
    if (p._ORDER_GUID && p._COMPANY_SOURCE) {
      return 'https://dh21ihyd55n14.cloudfront.net/empresas/' + p._COMPANY_SOURCE +
             '/orders/temp/' + p._ORDER_GUID + '/1.jpg';
    }
    var url = item.image || (item.featured_image && item.featured_image.url);
    if (!url) return null;
    return url.indexOf('?') === -1 ? url + '?width=200' : url + '&width=200';
  }

  function api(path, opts) {
    return fetch(root + path, Object.assign({
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }, opts || {})).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) throw j;
        return j;
      });
    });
  }

  /* ---------------------------------------------------------- the bag button */

  function buildPill() {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'mcb';
    b.setAttribute('aria-label', 'Open the bag');
    b.setAttribute('aria-haspopup', 'dialog');
    b.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="M6 8h12l1 12H5L6 8Z"></path><path d="M9 8V6a3 3 0 0 1 6 0v2"></path>' +
      '</svg>' +
      '<span class="mcb-badge" hidden>0</span>';
    b.addEventListener('click', function () { open(); });
    return b;
  }

  function ensureBadge(el) {
    var b = el.querySelector('.mcb-badge');
    if (!b) {
      b = document.createElement('span');
      b.className = 'mcb-badge';
      b.hidden = true;
      el.appendChild(b);
    }
    return b;
  }

  /* Matched two ways on purpose: data-mcb finds the button we have already
     adopted (whose label we rewrite with the count), the aria-label finds a
     fresh one after React has re-rendered the header. */
  function nativeBags() {
    return document.querySelectorAll(
      '#root header button[data-mcb], #root header button[aria-label="Open bag"]'
    );
  }

  function host() {
    return nativeBags().length ? true : document.querySelector('.mc-nav-in');
  }

  /* Two headers, one bag. On the home page the app already renders a bag
     button, so a second one would be noise — adopt theirs and hang the real
     count on it. Everywhere else, add ours to the Liquid nav. */
  function place() {
    if (!pill) return;
    var nats = nativeBags();

    /* The app ships two of these: one for the desktop nav, one for the
       mobile row. Only one is ever visible, so badge both. */
    if (nats.length) {
      if (pill.parentNode) pill.parentNode.removeChild(pill);
      bags = [];
      for (var i = 0; i < nats.length; i++) {
        nats[i].setAttribute('data-mcb', '1');
        ensureBadge(nats[i]);
        bags.push(nats[i]);
      }
      paintCount();
      return;
    }

    var nav = document.querySelector('.mc-nav-in');
    if (nav) {
      pill.classList.remove('mcb-float');
      if (pill.parentNode !== nav) nav.appendChild(pill);
    } else if (!pill.parentNode) {
      pill.classList.add('mcb-float');
      document.body.appendChild(pill);
    }
    bags = [pill];
    paintCount();
    tint();
  }

  /* The home page header is white-on-dark over the hero and changes tone as
     it scrolls; the Liquid nav is dark-on-light. Rather than guess, take the
     colour the neighbouring nav links are already using. */
  function tint() {
    if (!pill || pill.classList.contains('mcb-float')) return;
    var ref = document.querySelector('#root header nav a') ||
              document.querySelector('.mc-links a');
    if (!ref) return;
    var c = window.getComputedStyle(ref).color;
    if (c && c !== pill.style.color) pill.style.color = c;
  }

  function paintCount() {
    var n = cart ? (cart.item_count || 0) : 0;
    var txt = n > 9 ? '9+' : String(n);
    var label = n === 1 ? 'Open the bag, 1 piece' :
                n === 0 ? 'Open the bag, empty' : 'Open the bag, ' + n + ' pieces';
    bags.forEach(function (el) {
      var b = el.querySelector('.mcb-badge');
      if (!b) return;
      b.textContent = txt;
      b.hidden = n === 0;
      el.setAttribute('aria-label', label);
    });
  }

  /* ---------------------------------------------------------- the drawer */

  function buildDrawer() {
    drawer = document.createElement('div');
    drawer.className = 'mcd';
    drawer.innerHTML =
      '<div class="mcd-veil" data-close></div>' +
      '<div class="mcd-panel" role="dialog" aria-modal="true" aria-label="Your bag">' +
        '<div class="mcd-top">' +
          '<h2 class="mcd-ttl">The Bag</h2>' +
          '<button type="button" class="mcd-x" data-close>Close</button>' +
        '</div>' +
        '<div class="mcd-body"></div>' +
      '</div>';
    panel = drawer.querySelector('.mcd-panel');
    body = drawer.querySelector('.mcd-body');
    body.addEventListener('click', onQty);

    drawer.addEventListener('click', function (e) {
      if (e.target.hasAttribute && e.target.hasAttribute('data-close')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (!drawer.classList.contains('is-on')) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var f = panel.querySelectorAll('a[href], button:not([disabled])');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    document.body.appendChild(drawer);
  }

  function render(error) {
    if (!body) return;
    var items = (cart && cart.items) || [];
    var cur = (cart && cart.currency) || 'EUR';

    if (!items.length) {
      body.innerHTML =
        (error ? '<p class="mcd-err">' + esc(error) + '</p>' : '') +
        '<p class="mcd-empty">Your bag is empty.</p>';
      var f = drawer.querySelector('.mcd-foot');
      if (f) f.parentNode.removeChild(f);
      return;
    }

    var lis = items.map(function (it, i) {
      var img = itemImage(it);
      return '<li class="mcd-li">' +
        (img ? '<img class="mcd-img" src="' + esc(img) + '" alt="" width="72" height="72" loading="lazy" decoding="async">' : '') +
        '<div class="mcd-info">' +
          '<a class="mcd-name" href="' + esc(it.url || '#') + '">' + esc(it.product_title || it.title) + '</a>' +
          (it.variant_title ? '<p class="mcd-var">' + esc(it.variant_title) + '</p>' : '') +
          '<div class="mcd-row">' +
            '<span class="mcd-qty">' +
              '<button type="button" data-line="' + (i + 1) + '" data-to="' + (it.quantity - 1) + '" aria-label="Remove one">&minus;</button>' +
              '<span>' + it.quantity + '</span>' +
              '<button type="button" data-line="' + (i + 1) + '" data-to="' + (it.quantity + 1) + '" aria-label="Add one">+</button>' +
            '</span>' +
            '<span class="mcd-price">' + money(it.final_line_price, cur) + '</span>' +
          '</div>' +
        '</div>' +
      '</li>';
    }).join('');

    body.innerHTML =
      (error ? '<p class="mcd-err">' + esc(error) + '</p>' : '') +
      '<ul class="mcd-list">' + lis + '</ul>';

    var foot = drawer.querySelector('.mcd-foot');
    if (!foot) {
      foot = document.createElement('div');
      foot.className = 'mcd-foot';
      panel.appendChild(foot);
    }
    foot.innerHTML =
      '<dl class="mcd-sub"><dt>Subtotal</dt><dd>' + money(cart.total_price, cur) + '</dd></dl>' +
      '<p class="mcd-fine">Made to order in the atelier &middot; allow 4 to 6 weeks. ' +
        'Taxes and duties calculated at checkout.</p>' +
      '<button type="button" class="mcd-go">Proceed to checkout</button>' +
      '<a class="mcd-view" href="' + root + 'cart">View the full bag</a>';

    foot.querySelector('.mcd-go').addEventListener('click', function () {
      window.location.href = root + 'checkout';
    });
  }

  function onQty(e) {
    var b = e.target.closest && e.target.closest('.mcd-qty button');
    if (!b || busy) return;
    busy = true;
    b.disabled = true;
    api('cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: Number(b.dataset.line), quantity: Number(b.dataset.to) })
    }).then(function (c) {
      cart = c; paintCount(); render();
    })['catch'](function () {
      render('That change could not be applied. Please try again.');
    })['finally'](function () { busy = false; });
  }

  function open() {
    lastFocus = document.activeElement;
    drawer.classList.add('is-on');
    document.body.classList.add('mcd-lock');
    render();
    refresh().then(function () { render(); });
    var x = drawer.querySelector('.mcd-x');
    if (x) x.focus();
  }

  function close() {
    drawer.classList.remove('is-on');
    document.body.classList.remove('mcd-lock');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function refresh() {
    return api('cart.js').then(function (c) {
      cart = c; paintCount(); return c;
    })['catch'](function () { return null; });
  }

  /* ---------------------------------------------------------- add to bag */

  function bindNativeBag() {
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var b = e.target.closest('button[data-mcb], button[aria-label^="Open bag"]');
      if (!b || b === pill || !b.closest('#root')) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      open();
    }, true);
  }

  /* The product form is Shopify's own {% form 'product' %}. Left alone it
     posts and reloads onto /cart, which loses the page the visitor was on.
     Intercept it, add over fetch, and open the drawer instead. If anything
     throws, the native submit still runs. */
  function bindForms() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;
      if (busy) { e.preventDefault(); return; }

      var btn = form.querySelector('[type=submit]');
      if (form.querySelector('input[name=id]:required') && !form.querySelector('input[name=id]:checked')) {
        return; /* let the browser show its own "select a size" message */
      }

      e.preventDefault();
      busy = true;
      var label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

      api('cart/add.js', { method: 'POST', body: new FormData(form) })
        .then(function () { return refresh(); })
        .then(function () { open(); })
        ['catch'](function (err) {
          var msg = (err && (err.description || err.message)) ||
                    'This piece could not be added to the bag.';
          refresh().then(function () { open(); render(msg); });
        })
        ['finally'](function () {
          busy = false;
          if (btn) { btn.disabled = false; btn.textContent = label; }
        });
    }, true);
  }

  /* ---------------------------------------------------------- boot */

  function start() {
    injectCSS();
    buildDrawer();
    pill = buildPill();
    place();
    bindNativeBag();
    bindForms();
    refresh();

    /* The React app re-renders its header and would drop the bag with it. */
    try {
      var queued = false;
      var mo = new MutationObserver(function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          var nats = nativeBags();
          if (nats.length) {
            for (var i = 0; i < nats.length; i++) {
              if (!nats[i].querySelector('.mcb-badge')) { place(); return; }
            }
            return;
          }
          var nav = document.querySelector('.mc-nav-in');
          if (!pill.isConnected || (nav && pill.parentNode !== nav)) place();
        });
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {}

    /* The home page header only exists once React has mounted. */
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      place();
      if (tries > 40 || host()) clearInterval(t);
    }, 250);

    var tinting = false;
    window.addEventListener('scroll', function () {
      if (tinting) return;
      tinting = true;
      requestAnimationFrame(function () { tinting = false; tint(); });
    }, { passive: true });

    /* Coming back from the checkout via the back button serves a cached page. */
    window.addEventListener('pageshow', function (ev) { if (ev.persisted) refresh(); });
  }

  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();
